<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'TextTemplate',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'saved']);

  const props = defineProps({
    oid: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const objectTypes = ref([
    {
      value: 'invoice',
      name: $fn.value.pgettext('TextTemplate|Type', 'Invoice'),
    },
    {
      value: 'invoice_line',
      name: $fn.value.pgettext('TextTemplate|Type', 'Invoice line'),
    },
  ]);
  const formId = ref(null);
  const formName = ref('textTemplate');
  const labelAlign = ref('right');
  const container = ref(null);
  const item = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    objectTypes: objectTypes.value,
    formId: formId.value,
    formName: formName.value,
    labelAlign: labelAlign.value,
    container: container.value,
    item: item.value,
    ux: ux.value,
  }));

  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_container = async () => {
    return {
      self: $fn.value.urlFor('create_object_by_type', {
        object_type: 'text-templates',
      }),
      object: 'list',
      data: [],
    };
  };
  const $$load_item = async () => {
    return props.oid
      ? $fn.value.fetch('get_object_by_type', {
          parameters: { object_type: 'text-templates', id: props.oid },
          asResource: true,
        })
      : new $fn.value.Resource({
          name: null,
          object_type: null,
          condition: null,
          template: null,
          description: null,
        });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'objectTypes' },
      { name: 'formId' },
      { name: 'formName' },
      { name: 'labelAlign' },
      { name: 'container' },
      { name: 'item' },
      { name: 'ux' },
    ],
    loaders: {
      formId: $$load_formId,
      container: $$load_container,
      item: $$load_item,
    },
    ux: {},
    dataRefs: {
      objectTypes,
      formId,
      formName,
      labelAlign,
      container,
      item,
      ux,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.oid,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('oid');
    }
  );

  const act_2248226175642056_emit_pc4g9rdru_getActionArgs = async ($event) => {
    return {
      event: 'saved',
      value: $event.data.response.data,
    };
  };
  const act_2248226175642056_rest_ain0da3mw_getActionArgs = async ($event) => {
    return {
      method: 'save',
      container: container.value,
      resource: item.value,
    };
  };

  const act_7315092382398562_reloadComponentData_7y7odt6op =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_p8lcvrwji =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_7y7odt6op,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_p8lcvrwji],
    event: 'reload',
  });
  const act_2248226175642056_emit_pc4g9rdru = new actionTypes.EmitAction({
    actionArgs: act_2248226175642056_emit_pc4g9rdru_getActionArgs,
  });
  const inst_evh_2248226175642056_saved_0wxnahfoh =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_emit_pc4g9rdru,
      event: 'saved',
      displayName: 'emit',
    });
  const act_2248226175642056_rest_ain0da3mw = new actionTypes.RestAction({
    actionArgs: act_2248226175642056_rest_ain0da3mw_getActionArgs,
    displayName: 'save',
    events: [inst_evh_2248226175642056_saved_0wxnahfoh],
  });
  const inst_evh_2248226175642056_submit_yaoburfla =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_rest_ain0da3mw,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_2248226175642056_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2248226175642056_submit_yaoburfla],
    event: 'submit',
  });

  const actions = {
    inst_evh_7315092382398562_reload_p8lcvrwji,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_saved_0wxnahfoh,
    inst_evh_2248226175642056_submit_yaoburfla,
    save: inst_evh_2248226175642056_submit_yaoburfla,
    inst_evh_2248226175642056_submit,
  };

  const evh_7315092382398562_reload_p8lcvrwji = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_p8lcvrwji.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_saved_0wxnahfoh = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_saved_0wxnahfoh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_submit_yaoburfla = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_submit_yaoburfla.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_submit = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpForm
      :id="formId"
      :name="formName"
      :readonly="item.isReadOnly"
      class="row"
      v-if="loaded"
      @submit="evh_2248226175642056_submit($event, {})"
    >
      <ItpCol>
        <ItpFormGroup
          :label-cols-md="2"
          :content-cols-md="10"
          name="delivery_modes"
          :label="pgettext('TextTemplate', 'Object type')"
          :labelAlign="labelAlign"
          required
        >
          <ItpFormSelect2
            name="object_type"
            v-model="item.object_type"
            :value="item.object_type"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.object_type =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :options="objectTypes"
            valueField="value"
            textField="name"
            required
          >
          </ItpFormSelect2>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="2"
          :content-cols-md="10"
          name="name"
          :label="pgettext('TextTemplate', 'Name')"
          :labelAlign="labelAlign"
          required
        >
          <ItpFormInput
            name="name"
            v-model="item.name"
            :value="item.name"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.name =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            required
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="2"
          :content-cols-md="10"
          name="description"
          :label="pgettext('TextTemplate', 'Description')"
          :labelAlign="labelAlign"
        >
          <ItpFormInput
            name="description"
            v-model="item.description"
            :value="item.description"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.description =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="2"
          :content-cols-md="10"
          name="condition"
          :label="pgettext('TextTemplate', 'Condition')"
          :labelAlign="labelAlign"
        >
          <ItpFormTextarea
            name="condition"
            v-model="item.condition"
            :value="item.condition"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.condition =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
          >
          </ItpFormTextarea>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="2"
          :content-cols-md="10"
          name="condition"
          :label="pgettext('TextTemplate', 'Template')"
          :labelAlign="labelAlign"
          required
        >
          <ItpFormTextarea
            name="template"
            monospace
            v-model="item.template"
            :value="item.template"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.template =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            required
          >
          </ItpFormTextarea>
        </ItpFormGroup>
        <div>
          <ItpButton
            :text="pgettext('Button', 'Save')"
            type="submit"
            :form="formId"
            variant="primary"
            icon="fa-save"
            :spinning="actions.save && actions.save.isRunning"
            :disabled="!forms[formName] || !forms[formName].submittable"
            class="ms-auto"
          >
          </ItpButton>
        </div>
      </ItpCol>
    </ItpForm>
  </ItpUsercontrol>
</template>
